// @ts-nocheck
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  CheckSquare,
  AlertTriangle,
  Users,
  Phone,
  FileText,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MobilePageContainer } from '../ui/MobilePageContainer';
import { MobileListItem } from '../ui/MobileListItem';
import { MobileEmptyState } from '../ui/MobileEmptyState';
import { MobileFab } from '../ui/MobileFab';
import { CreateAppointmentModal } from '@/components/appointments/CreateAppointmentModal';
import { CreateTaskModal } from '@/components/tasks/CreateTaskModal';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

type AgendaItem = {
  id: string;
  kind: 'appointment' | 'task';
  title: string;
  when: Date;
  location?: string;
  appointment_type?: string;
  priority?: string;
  case_id?: string | null;
  raw: any;
};

const WEEK_DAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const startOfDay = (d: Date) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};
const endOfDay = (d: Date) => {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
};
const sameDay = (a: Date, b: Date) => startOfDay(a).getTime() === startOfDay(b).getTime();

const monthLabel = (d: Date) =>
  d
    .toLocaleString('pt-BR', { month: 'long', year: 'numeric' })
    .replace(/^./, (c) => c.toUpperCase());

const buildCalendarGrid = (viewDate: Date) => {
  const first = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const startWeekDay = first.getDay();
  const daysInMonth = new Date(
    viewDate.getFullYear(),
    viewDate.getMonth() + 1,
    0,
  ).getDate();
  const days: Array<Date | null> = [];
  for (let i = 0; i < startWeekDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
  }
  while (days.length % 7 !== 0) days.push(null);
  return days;
};

const APPOINTMENT_ICON: Record<string, any> = {
  audiencia: Users,
  atendimento: Phone,
  consulta: Phone,
  reuniao: Users,
  prazo: AlertTriangle,
  default: CalendarIcon,
};

const PRIORITY_DOT: Record<string, string> = {
  baixa: 'bg-sky-500',
  normal: 'bg-primary',
  média: 'bg-amber-500',
  media: 'bg-amber-500',
  alta: 'bg-orange-500',
  urgente: 'bg-rose-500',
  critica: 'bg-red-600',
  crítica: 'bg-red-600',
};

const MobileAgenda = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [viewDate, setViewDate] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [apptModalOpen, setApptModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
      const monthEnd = new Date(
        viewDate.getFullYear(),
        viewDate.getMonth() + 1,
        0,
        23,
        59,
        59,
      );

      const [apptRes, taskRes] = await Promise.all([
        supabase
          .from('appointments')
          .select(
            'id, title, appointment_date, duration, location, appointment_type, priority, case_id, description',
          )
          .eq('user_id', user.id)
          .gte('appointment_date', monthStart.toISOString())
          .lte('appointment_date', monthEnd.toISOString())
          .order('appointment_date', { ascending: true }),
        supabase
          .from('tasks')
          .select('id, title, due_date, priority, case_id, description, status')
          .eq('user_id', user.id)
          .not('due_date', 'is', null)
          .gte('due_date', monthStart.toISOString())
          .lte('due_date', monthEnd.toISOString())
          .order('due_date', { ascending: true }),
      ]);

      const appts: AgendaItem[] = (apptRes.data || []).map((a: any) => ({
        id: a.id,
        kind: 'appointment',
        title: a.title,
        when: new Date(a.appointment_date),
        location: a.location,
        appointment_type: a.appointment_type,
        priority: a.priority,
        case_id: a.case_id,
        raw: a,
      }));
      const tasks: AgendaItem[] = (taskRes.data || [])
        .filter((t: any) => t.status !== 'Concluída')
        .map((t: any) => ({
          id: t.id,
          kind: 'task',
          title: t.title,
          when: new Date(t.due_date),
          priority: t.priority,
          case_id: t.case_id,
          raw: t,
        }));

      setItems(
        [...appts, ...tasks].sort(
          (a, b) => a.when.getTime() - b.when.getTime(),
        ),
      );
    } catch (err: any) {
      toast({
        title: 'Erro ao carregar agenda',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [user, viewDate, toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Contagem por dia (para bolinhas no calendário)
  const itemsByDay = useMemo(() => {
    const map = new Map<string, AgendaItem[]>();
    items.forEach((i) => {
      const k = startOfDay(i.when).toISOString();
      const arr = map.get(k) || [];
      arr.push(i);
      map.set(k, arr);
    });
    return map;
  }, [items]);

  const daysGrid = useMemo(() => buildCalendarGrid(viewDate), [viewDate]);

  const dayItems = useMemo(() => {
    const s = startOfDay(selectedDate).getTime();
    const e = endOfDay(selectedDate).getTime();
    return items.filter((i) => {
      const t = i.when.getTime();
      return t >= s && t <= e;
    });
  }, [items, selectedDate]);

  const today = startOfDay(new Date());

  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <MobilePageContainer>
      {/* Calendário */}
      <div className="bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h2 className="font-bold text-base tracking-tight">
            {monthLabel(viewDate)}
          </h2>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() =>
                setViewDate(
                  new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1),
                )
              }
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-2 text-xs"
              onClick={() => {
                const t = startOfDay(new Date());
                setViewDate(t);
                setSelectedDate(t);
              }}
            >
              Hoje
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() =>
                setViewDate(
                  new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1),
                )
              }
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-7 px-2 pb-1 text-[10px] font-semibold text-muted-foreground">
          {WEEK_DAYS.map((d, i) => (
            <div key={i} className="text-center py-1">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 px-1 pb-3 gap-0.5">
          {daysGrid.map((d, idx) => {
            if (!d) return <div key={idx} className="h-11" />;
            const isSel = sameDay(d, selectedDate);
            const isToday = sameDay(d, today);
            const dayEvents =
              itemsByDay.get(startOfDay(d).toISOString()) || [];
            const hasAppt = dayEvents.some((e) => e.kind === 'appointment');
            const hasTask = dayEvents.some((e) => e.kind === 'task');

            return (
              <button
                key={idx}
                onClick={() => setSelectedDate(d)}
                className={cn(
                  'h-11 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-colors active:scale-95',
                  isSel
                    ? 'bg-primary text-primary-foreground'
                    : isToday
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-muted',
                )}
              >
                <span className="text-[13px] font-semibold leading-none">
                  {d.getDate()}
                </span>
                <span className="flex gap-0.5 h-1">
                  {hasAppt && (
                    <span
                      className={cn(
                        'h-1 w-1 rounded-full',
                        isSel ? 'bg-primary-foreground' : 'bg-sky-500',
                      )}
                    />
                  )}
                  {hasTask && (
                    <span
                      className={cn(
                        'h-1 w-1 rounded-full',
                        isSel ? 'bg-primary-foreground' : 'bg-amber-500',
                      )}
                    />
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lista do dia */}
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <h3 className="font-bold text-base tracking-tight">
          {selectedDate.toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
          })}
        </h3>
        <span className="text-[11px] text-muted-foreground">
          {dayItems.length} item{dayItems.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="px-4 py-6 text-sm text-muted-foreground text-center">
          Carregando...
        </div>
      ) : dayItems.length === 0 ? (
        <MobileEmptyState
          className="mt-4"
          icon={<CalendarIcon className="h-6 w-6" />}
          title="Sem compromissos neste dia"
          description="Toque em + para adicionar."
        />
      ) : (
        <div className="mx-3 rounded-2xl border border-border bg-card overflow-hidden divide-y divide-border">
          {dayItems.map((i) => {
            const Icon =
              i.kind === 'appointment'
                ? APPOINTMENT_ICON[i.appointment_type || ''] ||
                  APPOINTMENT_ICON.default
                : CheckSquare;
            const prDot = i.priority
              ? PRIORITY_DOT[i.priority.toLowerCase()]
              : null;
            return (
              <MobileListItem
                key={`${i.kind}-${i.id}`}
                icon={<Icon className="h-5 w-5" />}
                iconBg={
                  i.kind === 'appointment'
                    ? 'bg-sky-500/15 text-sky-600 dark:text-sky-400'
                    : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                }
                title={i.title}
                subtitle={
                  <>
                    <span>
                      {fmtTime(i.when)}
                      {i.kind === 'appointment' && i.appointment_type
                        ? ` · ${i.appointment_type}`
                        : ''}
                    </span>
                    {i.location && (
                      <span className="block truncate">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {i.location}
                      </span>
                    )}
                  </>
                }
                badges={
                  <>
                    {prDot && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                        <span className={cn('h-2 w-2 rounded-full', prDot)} />
                        {i.priority}
                      </span>
                    )}
                    {i.case_id && (
                      <Badge className="bg-primary/15 text-primary border-0 text-[10px]">
                        <FileText className="h-3 w-3 mr-1" />
                        Processo
                      </Badge>
                    )}
                  </>
                }
                onClick={() => {
                  if (i.kind === 'task') {
                    if (i.case_id) {
                      navigate(`/pwa/processo/${i.case_id}`);
                    } else {
                      // Sem case_id: abre a tela de tarefas para edição/ações
                      navigate('/pwa/tasks');
                    }
                    return;
                  }
                  // appointments continuam na própria agenda
                }}
              />
            );
          })}
        </div>
      )}

      <MobileFab onClick={() => setCreateSheetOpen(true)} />

      {/* Bottom sheet: escolher o que criar */}
      <Sheet open={createSheetOpen} onOpenChange={setCreateSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>O que você quer adicionar?</SheetTitle>
          </SheetHeader>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              onClick={() => {
                setCreateSheetOpen(false);
                setTimeout(() => setApptModalOpen(true), 100);
              }}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl border border-border bg-card active:bg-muted"
            >
              <div className="h-12 w-12 rounded-xl bg-sky-500/15 text-sky-600 flex items-center justify-center">
                <Phone className="h-5 w-5" />
              </div>
              <span className="font-semibold text-sm">Atendimento</span>
            </button>
            <button
              onClick={() => {
                setCreateSheetOpen(false);
                setTimeout(() => setTaskModalOpen(true), 100);
              }}
              className="flex flex-col items-center gap-2 p-4 rounded-2xl border border-border bg-card active:bg-muted"
            >
              <div className="h-12 w-12 rounded-xl bg-amber-500/15 text-amber-600 flex items-center justify-center">
                <CheckSquare className="h-5 w-5" />
              </div>
              <span className="font-semibold text-sm">Tarefa / Prazo</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <CreateAppointmentModal
        isOpen={apptModalOpen}
        onClose={() => setApptModalOpen(false)}
        selectedDate={selectedDate}
        onAppointmentCreated={() => {
          setApptModalOpen(false);
          load();
        }}
      />

      <CreateTaskModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        selectedDate={selectedDate}
        onTaskCreated={() => {
          setTaskModalOpen(false);
          load();
        }}
      />
    </MobilePageContainer>
  );
};

export default MobileAgenda;
